import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Slider } from 'primereact/slider';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { baseUrlAdmin } from '../../store/apiConstants';
import Service from '../../services';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import './RiskConfiguration.scss';

class RiskConfiguration extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        
        this.state = {
            loading: false,
            saving: false,
            weights: {
                paymentDelay: 40,
                brokenPromise: 20,
                currentOverdue: 10,
                discountStress: 10,
                engagementPenalty: 20
            },
            thresholds: {
                criticalRiskScore: 75,
                highRiskScore: 50,
                mediumRiskScore: 25
            }
        };
        
        this.toast = React.createRef();
    }

    componentDidMount() {
        this.fetchConfig();
    }

    fetchConfig = async () => {
        this.setState({ loading: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const url = `${baseUrlAdmin}/fee-decision/risk/config?academicYear=${academicYear}`; // Use URL params for GET
            
            const response = await this.service.get(url, true);
            
            if (response && response.status && response.res.data) {
                const { weights, thresholds } = response.res.data;
                // Merge with defaults to ensure all fields exist
                this.setState({
                    weights: { ...this.state.weights, ...weights },
                    thresholds: { ...this.state.thresholds, ...thresholds }
                });
            }
        } catch (error) {
            console.error('Error fetching risk config:', error);
            this.showToast('error', 'Error', 'Failed to fetch configuration');
        } finally {
            this.setState({ loading: false });
        }
    };

    saveConfig = async () => {
        const totalWeight = this.calculateTotalWeight();
        if (totalWeight !== 100) {
            this.showToast('warn', 'Invalid Configuration', 'Total weight must equal 100');
            return;
        }

        this.setState({ saving: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const body = {
                academicYear,
                weights: this.state.weights,
                thresholds: this.state.thresholds
            };

            const url = `${baseUrlAdmin}/fee-decision/risk/config`;
            const response = await this.service.post(url, body, true);

            if (response && response.status) {
                this.showToast('success', 'Success', 'Configuration saved successfully');
            } else {
                this.showToast('error', 'Error', 'Failed to save configuration');
            }
        } catch (error) {
            console.error('Error saving risk config:', error);
            this.showToast('error', 'Error', 'Failed to save configuration');
        } finally {
            this.setState({ saving: false });
        }
    };

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    handleWeightChange = (key, value) => {
        this.setState(prevState => ({
            weights: {
                ...prevState.weights,
                [key]: value
            }
        }));
    };

    handleThresholdChange = (key, value) => {
        this.setState(prevState => ({
            thresholds: {
                ...prevState.thresholds,
                [key]: value
            }
        }));
    };

    calculateTotalWeight = () => {
        const { weights } = this.state;
        return Object.values(weights).reduce((a, b) => a + b, 0);
    };

    render() {
        const { loading, saving, weights, thresholds } = this.state;
        const totalWeight = this.calculateTotalWeight();
        const isWeightValid = totalWeight === 100;

        if (loading) {
            return <LoadingComponent />;
        }

        return (
            <div className="risk-config-container">
                <Toast ref={this.toast} position="bottom-right" />
                
                <div className="flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2>Risk Scoring Configuration</h2>
                        <p className="text-secondary m-0">Adjust the impact of different factors on the student risk score.</p>
                    </div>
                </div>

                <div className="config-section">
                    <div className="section-header">
                        <h3>Scoring Weights</h3>
                        <p>Define how much each factor contributes to the total risk score (0-100).</p>
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6 lg:col-4">
                            <div className="weight-item">
                                <label>Payment Delay Score (Max {weights.paymentDelay})</label>
                                <InputNumber 
                                    value={weights.paymentDelay} 
                                    onValueChange={(e) => this.handleWeightChange('paymentDelay', e.value)} 
                                    showButtons min={0} max={100} 
                                    suffix="%"
                                    className="w-full"
                                />
                                <div className="helper-text">Impact of historical payment delays</div>
                            </div>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                            <div className="weight-item">
                                <label>Broken Promise Score (Max {weights.brokenPromise})</label>
                                <InputNumber 
                                    value={weights.brokenPromise} 
                                    onValueChange={(e) => this.handleWeightChange('brokenPromise', e.value)} 
                                    showButtons min={0} max={100} 
                                    suffix="%"
                                    className="w-full"
                                />
                                <div className="helper-text">Impact of breached payment promises</div>
                            </div>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                            <div className="weight-item">
                                <label>Current Overdue (Max {weights.currentOverdue})</label>
                                <InputNumber 
                                    value={weights.currentOverdue} 
                                    onValueChange={(e) => this.handleWeightChange('currentOverdue', e.value)} 
                                    showButtons min={0} max={100} 
                                    suffix="%"
                                    className="w-full"
                                />
                                <div className="helper-text">Impact of currently active overdue</div>
                            </div>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                            <div className="weight-item">
                                <label>Discount Stress (Max {weights.discountStress})</label>
                                <InputNumber 
                                    value={weights.discountStress} 
                                    onValueChange={(e) => this.handleWeightChange('discountStress', e.value)} 
                                    showButtons min={0} max={100} 
                                    suffix="%"
                                    className="w-full"
                                />
                                <div className="helper-text">Impact of high discount reliance</div>
                            </div>
                        </div>
                        <div className="col-12 md:col-6 lg:col-4">
                            <div className="weight-item">
                                <label>Engagement Penalty (Max {weights.engagementPenalty})</label>
                                <InputNumber 
                                    value={weights.engagementPenalty} 
                                    onValueChange={(e) => this.handleWeightChange('engagementPenalty', e.value)} 
                                    showButtons min={0} max={100} 
                                    suffix="%"
                                    className="w-full"
                                />
                                <div className="helper-text">Impact of ignored communications</div>
                            </div>
                        </div>
                    </div>

                    <div className={`total-weight-indicator ${isWeightValid ? 'valid' : 'invalid'}`}>
                        <span>Total Calculation Weight</span>
                        <div className="flex align-items-center gap-3">
                            <span className="weight-value">{totalWeight}%</span>
                            {!isWeightValid && <Message severity="error" text="Must sum to 100%" />}
                            {isWeightValid && <i className="pi pi-check-circle" style={{fontSize: '1.5rem'}} />}
                        </div>
                    </div>
                </div>

                <div className="config-section">
                    <div className="section-header">
                        <h3>Risk Thresholds</h3>
                        <p>Determine the cut-off points for risk categories.</p>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <div className="threshold-item">
                                <label>Critical Risk Threshold (Currently {thresholds.criticalRiskScore}+)</label>
                                <Slider 
                                    value={thresholds.criticalRiskScore} 
                                    onChange={(e) => this.handleThresholdChange('criticalRiskScore', e.value)} 
                                    min={50} max={100} 
                                />
                                <div className="helper-text flex justify-content-between mt-1">
                                    <span>50</span>
                                    <span>{thresholds.criticalRiskScore}</span>
                                    <span>100</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="threshold-item">
                                <label>High Risk Threshold (Currently {thresholds.highRiskScore}+)</label>
                                <Slider 
                                    value={thresholds.highRiskScore} 
                                    onChange={(e) => this.handleThresholdChange('highRiskScore', e.value)} 
                                    min={25} max={75} 
                                />
                                <div className="helper-text flex justify-content-between mt-1">
                                    <span>25</span>
                                    <span>{thresholds.highRiskScore}</span>
                                    <span>75</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="actions-bar">
                    <Button 
                        label="Reset to Defaults" 
                        icon="pi pi-refresh" 
                        className="p-button-outlined p-button-secondary" 
                        onClick={() => this.setState({
                            weights: { paymentDelay: 40, brokenPromise: 20, currentOverdue: 10, discountStress: 10, engagementPenalty: 20 },
                            thresholds: { criticalRiskScore: 75, highRiskScore: 50, mediumRiskScore: 25 }
                        })}
                    />
                    <Authorizer permId={PERMISSIONS.FDE_UPDATE_RISK_CONFIGURATION}>
                        <Button 
                            label="Save Configuration" 
                            icon="pi pi-save" 
                            loading={saving} 
                            onClick={this.saveConfig}
                            disabled={!isWeightValid}
                            className="p-button-primary"
                        />
                    </Authorizer>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    userInfo: state.currentUserPerms ? state.currentUserPerms.userInfo : {}
});

export default connect(mapStateToProps)(RiskConfiguration);
